<i18n
  src="@/locales/views/packing_house/production/final_packing_list_simplified/assign_packages.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_container')"
      :params="{
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
        container_id: this.container_id,
      }"
    ></go-back-link>

    <bread-crumbs
      :labels="[module_name, packing_house_process?.name, container_label]"
    />

    <div class="flex my-8 justify-end">
      <button
        v-for="(weekNumber, index) in weeks"
        :key="index"
        :class="[
          'flex items-center justify-between px-8 border border-gray-300 space-x-2',
          {
            'rounded-l-xl': index === 0,
            'rounded-r-xl': index === weeks.length - 1,
            'border-green-600': filterWeeks.includes(weekNumber),
          },
        ]"
        @click.prevent="toggleFilterWeek(weekNumber)"
      >
        <i
          :class="[
            'fas fa-circle-check ',
            filterWeeks.includes(weekNumber)
              ? 'text-green-600'
              : 'text-gray-400',
          ]"
        />

        <h3 class="text-gray-700">Sem {{ weekNumber }}</h3>
      </button>

      <div class="ml-4 flex justify-center">
        <white-button
          @click.prevent="fetchUnassignedPackages()"
          :label="$t('labels.filter_button')"
        />
      </div>
    </div>

    <div class="flex flex-col">
      <h3 class="text-right py-1 pr-2">
        {{ $t("labels.total_packages") }}: {{ total_packages }}
      </h3>

      <ag-grid-vue
        style="width: 100%; height: 600px"
        class="ag-theme-alpine w-full h-full"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef()"
        :rowData="unassigned_packages"
        @grid-ready="onGridReady"
        @cellValueChanged="cellValueChanged"
      />
      <h3 class="text-right py-1 pr-2">
        {{ $t("labels.total_packages") }}: {{ total_packages }}
      </h3>
    </div>
    <div class="pt-2 pb-6 text-right">
      <white-button
        @click.prevent="goToContainer"
        :label="$t('labels.cancel_button')"
      />
      <span class="space-actions"></span>
      <success-button
        v-if="!proc_save_button"
        @click.prevent="submit"
        :label="$t('labels.save_button')"
      />
      <success-button-processing v-else :label="$t('labels.save_button')" />
    </div>
  </div>
</template>

<script>
import Requester from "@/views/packing_house/requesters/production/final_packing_list_simplified/assign_packages.js";

import { AgGridVue } from "ag-grid-vue3";

export default {
  name: "PHProductionFinalPackingListSimplifiedAssignPackages",
  components: {
    AgGridVue,
  },
  data() {
    return {
      packing_house_process: null,
      container: null,
      load_general_data: false,
      filterWeeks: [],
      unassigned_packages: [],
      assigned_packages: [],
      packages: [],
      proc_save_button: false,
    };
  },
  props: {
    packing_house_id: { required: true },
    packing_house_process_id: { required: true },
    container_id: { required: true },
  },

  computed: {
    container_code() {
      return this.container ? this.container.container_code : "";
    },
    container_label: function () {
      let label = this.container_code;
      const brand_name = this.container?.brand_name;
      if (brand_name) label = `${label} (${brand_name})`;
      return label;
    },
    weeks: function () {
      if (this.container) {
        const currentWeek = this.container.week;
        const start = Math.max(0, currentWeek - 4);
        return Array.from(
          { length: (Math.min(54, currentWeek + 2) - start) / 1 + 1 },
          (_, index) => start + index * 1
        );
      }
      return [];
    },
    columnDefs: function () {
      return [
        {
          headerName: this.$t("model.package.week"),
          field: "week",
          minWidth: 75,
          width: 100,
        },
        {
          headerName: this.$t("model.package.registered_at"),
          field: "registered_at",
          flex: 1,
          minWidth: 100,
          filter: true,
          cellRenderer: (params) => this.dateFormatter(params.value, "d MMM"),
        },
        {
          headerName: this.$t("model.package.producer"),
          field: "producer.fullname",
          flex: 2,
          filter: true,
        },
        {
          headerName: this.$t("model.exported_batch.crop_field"),
          field: "exported_batch.crop_field.name",
          flex: 1,
          filter: true,
        },
        {
          headerName: this.$t("model.package.exporter_process_crop_package"),
          field: "exporter_process_crop_package.name",
          flex: 1,
          filter: true,
          cellRenderer: (params) => {
            return `${params.value} - ${params.data.exporter_process_brand.name}`;
          },
        },
        {
          headerName: this.$t("labels.quantity_available"),
          field: "quantity_available",
          flex: 1,
        },
        {
          headerName: this.$t("labels.quantity_assigned"),
          field: "quantity_assigned",
          flex: 1,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellEditorParams: {
            inputType: "number",
          },
          cellStyle: (params) => {
            const total =
              Number(params.data.quantity_assigned) +
              params.data.quantity_available;
            return {
              color: total > params.data.quantity ? "#991b1b" : "black",
              backgroundColor:
                total > params.data.quantity ? "#fecaca" : "transparent",
            };
          },
        },
      ];
    },
    total_packages: function () {
      return this.packages.reduce((total, pkg) => {
        return total + (Number(pkg.quantity) || 0);
      }, 0);
    },
    packages_params: function () {
      return this.packages
        .filter((pkg) => Number(pkg.quantity) > 0 || pkg._destroy)
        .map((pkg) => {
          let params = {
            id: pkg.id,
            quantity: pkg.quantity,
          };
          if (pkg._destroy) {
            params._destroy = true;
          }
          return params;
        });
    },
  },
  methods: {
    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess(
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.packing_house_process_id,
        this.container_id
      );
      this.container = response.data;
      this.filterWeeks.push(this.container.week);
    },
    fetchUnassignedPackages: async function () {
      this.load_general_data = false;
      let response = await Requester.fetchUnassignedPackages(
        this.packing_house_process_id,
        this.container_id,
        this.filterWeeks
      );
      this.filterWeeks = response.data.weeks;
      this.unassigned_packages = response.data.unassigned;
      this.assigned_packages = response.data.assigned;
      this.unassigned_packages.forEach((pkg) => {
        let assigned = this.assigned_packages.find(
          (assignedPkg) => assignedPkg.id === pkg.id
        );
        pkg.quantity_assigned = assigned?.quantity || null;
        pkg.quantity_available = pkg.quantity - (assigned?.quantity || 0);
      });

      this.packages = this.unassigned_packages.map((pkg) => {
        return {
          id: pkg.id,
          quantity: pkg.quantity_assigned || null,
          _destroy: false,
        };
      });
      this.load_general_data = true;
    },
    submit: async function () {
      this.proc_save_button = true;
      try {
        const response = await Requester.assignPackages(
          this.packing_house_process_id,
          this.container_id,
          this.packages_params
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToContainer();
      } catch (error) {
        console.error(error);
      } finally {
        this.proc_save_button = false;
      }
    },
    toggleFilterWeek: function (weekNumber) {
      const index = this.filterWeeks.indexOf(weekNumber);
      if (index > -1) {
        this.filterWeeks.splice(index, 1);
      } else {
        this.filterWeeks.push(weekNumber);
      }
    },
    onGridReady: function (params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      params.api.sizeColumnsToFit();
    },

    defaultColDef: function () {
      return {
        sortable: true,
        floatingFilter: true,
        resizable: true,
      };
    },

    cellValueChanged: function (event) {
      const index = this.packages.findIndex((pkg) => pkg.id === event.data.id);
      const is_assigned = this.assigned_packages.some(
        (pkg) => pkg.id === event.data.id
      );
      if (index !== -1) {
        if (Number(event.data.quantity_assigned) < 0) {
          event.data.quantity_assigned = 0;
        }
        this.packages[index]._destroy =
          is_assigned &&
          (event.data.quantity_assigned === null ||
            event.data.quantity_assigned === "" ||
            Number(event.data.quantity_assigned) === 0);

        this.packages[index].quantity = event.data.quantity_assigned;
        const new_available =
          event.data.quantity - event.data.quantity_assigned;
        event.data.quantity_available = new_available < 0 ? 0 : new_available;
      }
    },
    goToContainer: function () {
      this.$router.push({
        name: this.routeName("show_container"),
        params: {
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          container_id: this.container_id,
        },
      });
    },
  },
  created: function () {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.fetchPackingHouseProcess();
        this.fetchContainer();
        this.fetchUnassignedPackages();
      })
    );
  },
};
</script>
